import type { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query/react';
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { config } from 'src/services/config';
import { RootState } from './store';

export enum SortDir {
  ASC = 'ASC',
  DESC = 'DESC'
}

export interface PaginableMeta {
  limit: number;
  page: number;
  total: number;
  totalPages: number;
}

export interface PaginableList<T> {
  items: T[];
  meta: PaginableMeta;
}

export interface PaginableResponse<T> {
  data: PaginableList<T>;
}

export interface PaginableRequest {
  limit?: number;
  page?: number;
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: config.apiUrl,
  prepareHeaders: (headers, api) => {
    const token = (api.getState() as RootState).auth.accessToken;

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  },
});

let refreshPromise: Promise<string | false> | null = null;

async function doRefresh(refreshToken: string, dispatch: (a: unknown) => void): Promise<string | false> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async (): Promise<string | false> => {
    try {
      const res = await fetch(`${config.apiUrl}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!res.ok) {
        dispatch({ type: 'auth/logout' });
        return false;
      }

      const json = await res.json();
      const newToken: string = json?.data?.accessToken;

      if (!newToken) {
        dispatch({ type: 'auth/logout' });
        return false;
      }

      dispatch({ type: 'auth/setAccessToken', payload: newToken });
      return newToken;
    } catch {
      dispatch({ type: 'auth/logout' });
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}, FetchBaseQueryMeta> =
  async (args, api, extraOptions) => {
    let result = await rawBaseQuery(args, api, extraOptions);

    if (result.error?.status === 401) {
      const refreshToken = (api.getState() as RootState).auth.refreshToken;

      if (!refreshToken) {
        api.dispatch({ type: 'auth/logout' });
        return result;
      }

      const newToken = await doRefresh(refreshToken, api.dispatch);

      if (newToken) {
        result = await rawBaseQuery(args, api, extraOptions);
      }
    }

    return result;
  };
